<template>
  <div>
    <p class="mb-5">{{ description || 'Estimated date of next service:' }}</p>
    <p class="mb-10">
      Select from one of the options below or use the calendar to provide a date
      and time for the service visit
    </p>
    <v-row align="center" dense class="mb-5">
      <v-col>
        <v-item-group v-model="etaWithinDays">
          <v-row dense>
            <v-col
              v-for="option in etaOptions"
              :key="option.value"
              cols="12"
              lg="">
              <v-item v-slot="{ active, toggle }" :value="option.value">
                <EsButtonAction
                  @click="toggle"
                  block
                  :label="option.text"
                  :active="active" />
              </v-item>
            </v-col>
          </v-row>
        </v-item-group>
      </v-col>
    </v-row>
    <div>
      <v-checkbox
        :disabled="!hasSelectedOtherOption"
        v-if="allowASAP && hasSelectedOtherOption"
        label="Set as ASAP"
        v-model="details.isASAP"
        @change="toggleASAP($event, i)"
      ></v-checkbox>
      <div class="text-right text-caption grey--text text--darken-1">
        required
      </div>
      <EsDatePicker
        v-if="!isASAP"
        allowRange
        clearable
        :disabled="etaWithinDays >= 0 && etaWithinDays != null || isASAP"
        :initialDate="formatedDateRangeForPicker"
        @setDate="saveDate($event)"
        required />
      <v-row v-if="!isASAP && (!details.vendorEtaEndDate || rangeIsSameDate)">
        <v-col>
          <EsTimePicker
            :initialTime="details.vendorEtaStartTime"
            :hoursOnly="true"
            :formatType="'hh A'"
            :returnedFormat="'hh A'"
            :placeholder="'Start Time'"
            @setTime="setStartTime" />
        </v-col>
        <v-col>
          <EsTimePicker
            :initialTime="details.vendorEtaEndTime"
            :hoursOnly="true"
            :formatType="'hh A'"
            :returnedFormat="'hh A'"
            :placeholder="'End Time'"
            @setTime="setEndTime" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import {
  EsButtonAction
} from "@86-repairs/design-system";
import EsDatePicker from './EsDatePicker.vue';
import EsTimePicker from './EsTimePicker.vue';

dayjs.extend(dayjsBusinessDays);

export default {
  name: 'EsEtaPicker',
    components: {
      EsButtonAction,
      EsDatePicker,
      EsTimePicker
  },
  props: {
    description: {
      type: String,
    },
    allowASAP: {
      type: Boolean,
      default: false,
    },
    prepopulatedEta: {
      type: Object,
      required: false,
    },
  },
  data: () => ({
    etaOptions: [
      { text: 'Today', value: 0 },
      { text: 'Tomorrow', value: 1 },
      { text: 'Within 3 business days', value: 3 },
      { text: 'Within 5 business days', value: 5 },
      { text: 'Other', value: -1 },
    ],
    details: {
      vendorEtaStartDate: undefined,
      vendorEtaEndDate: undefined,
      vendorEtaStartTime: undefined,
      vendorEtaEndTime: undefined,
      etaUnknown: false,
    },
    etaWithinDays: null,
  }),
  computed: {
    isASAP() {
      return this.details.etaUnknown === true;
    },
    hasSelectedOtherOption(){
      return this.etaWithinDays === -1;
    },
    rangeIsSameDate() {
      return (
        this.details?.vendorEtaStartDate === this.details?.vendorEtaEndDate
      );
    },
    formatedDateRangeForPicker() {
      if (this.details.vendorEtaStartDate) {
        return [this.details.vendorEtaStartDate, this.details.vendorEtaEndDate];
      }
      return null;
    },
  },
  watch: {
    prepopulatedEta: {
      handler(value) {
        if (value?.definedEta) {
          switch (value.definedEta) {
            case 'today':
              this.etaWithinDays = 0;
              this.details.vendorEtaStartTime = value.etaStartTime;
              this.details.vendorEtaEndTime = value.etaEndTime;
              break;
            case 'tomorrow':
              this.etaWithinDays = 1;
              this.details.vendorEtaStartTime = value.etaStartTime;
              this.details.vendorEtaEndTime = value.etaEndTime;
              break;
            case 'within 3 business days':
              this.etaWithinDays = 3;
              break;
            case 'within 5 business days':
              this.etaWithinDays = 5;
              break;
            default:
              return;
          }
        }
      },
      deep: true,
      immediate: true,
    },
    details: {
      handler() {
        this.$emit('input', this.details);
      },
      deep: true,
    },
    etaWithinDays(value) {
      if (value !== -1) {
        this.details.etaUnknown = false;
      }
      if (value === 0) {
        this.details.vendorEtaStartDate = dayjs().format('YYYY-MM-DD');
        this.details.vendorEtaEndDate = undefined;
      }
      if (value === 1) {
        this.details.vendorEtaStartDate = dayjs()
          .add(1, 'day')
          .format('YYYY-MM-DD');
        this.details.vendorEtaEndDate = undefined;
      }
      if (value === 3 || value === 5) {
        this.details.vendorEtaStartDate = dayjs()
          .businessDaysAdd(1)
          .format('YYYY-MM-DD');
        this.details.vendorEtaEndDate = dayjs()
          .businessDaysAdd(value)
          .format('YYYY-MM-DD');
      }
      
    },
    'details.vendorEtaEndDate': {
      handler(value) {
        if (value) {
          this.details.vendorEtaStartTime = undefined;
          this.details.vendorEtaEndTime = undefined;
          this.details.etaUnknown = false;
        }
      },
    },
  },
  methods: {
    saveDate(date) {
      if (date) {
        this.details.vendorEtaStartDate = date[0];
        this.details.vendorEtaEndDate = date[1];
      } else {
        this.details.vendorEtaStartDate = null;
        this.details.vendorEtaEndDate = null;
      }
    },
    setStartTime(time) {
      if (dayjs(time, 'hh A', true).isValid()) {
        this.details.vendorEtaStartTime = dayjs(time, 'hh A').format('hh:mm A');
      }
    },
    setEndTime(time) {
      if (dayjs(time, 'hh A', true).isValid()) {
        this.details.vendorEtaEndTime = dayjs(time, 'hh A').format('hh:mm A');
      }
    },
    toggleASAP(isNowASAP) {
      this.details.vendorEtaStartDate = null;
      this.details.vendorEtaEndDate = null;
      this.details.vendorEtaStartTime = null;
      this.details.vendorEtaEndTime = null;
      this.details.etaUnknown = isNowASAP;
    },
  },
};
</script>
